import { SIGNUP_FLOW_ROOT } from '@/config/signup-flow-root';
import type { LegacySignupParams } from './services/legacy-params';
import { BASELINE_STEPS } from './baseline/baseline-steps';
import { SCREENING_STEPS } from './screening/screening-steps';
import { SIGNUP_TERMINAL_STEPS } from './minor/minor-steps';
import { WRAP_UP_STEPS } from './wrap-up/wrap-up-steps';

/** The flow's entry route. It reads the params, then replaces itself with the first step. */
export const SIGNUP_ENTRY_PATH = SIGNUP_FLOW_ROOT;

/**
 * Ordered step ids, in v9 main-path order. Each is a static page at
 * `/<id>/` and a registered screen.
 */
export const SIGNUP_STEPS = [
  'context',
  'substance',
  'legal-name',
  'account',
  'code',
  'plan-details',
  'address',
  'birth-date',
  'contact',
  'quiz-motivation',
  'quiz-goal',
  ...BASELINE_STEPS,
  ...SCREENING_STEPS,
  ...WRAP_UP_STEPS,
  'download',
] as const;

/**
 * Steps only the no-employer path visits: the short eligibility wait after the
 * code, and the employer or health plan search when it finds no match.
 */
export const NO_EMPLOYER_ONLY_STEPS = ['eligibility-check', 'employer-search'] as const;

/**
 * Steps only the employer SSO path visits: the choice of roster contact the
 * code goes to, and the code itself. They stand in for the account and code
 * steps, since the employer already proved who the member is.
 */
export const SSO_ONLY_STEPS = ['sso-contact', 'sso-code'] as const;

/**
 * Every step id: the ordered steps of every path plus the steps that end the
 * flow, which are pages but never a "next" step (see `SIGNUP_TERMINAL_STEPS`).
 */
export type SignupStepId =
  | (typeof SIGNUP_STEPS)[number]
  | (typeof NO_EMPLOYER_ONLY_STEPS)[number]
  | (typeof SSO_ONLY_STEPS)[number]
  | (typeof SIGNUP_TERMINAL_STEPS)[number];

/** The main path's first step. */
export const FIRST_SIGNUP_STEP: SignupStepId = SIGNUP_STEPS[0];

/** Every step id, each a static page at `/<id>/`. */
export const ALL_SIGNUP_STEPS: readonly SignupStepId[] = [
  ...SIGNUP_STEPS,
  ...NO_EMPLOYER_ONLY_STEPS,
  ...SSO_ONLY_STEPS,
  ...SIGNUP_TERMINAL_STEPS,
];

/**
 * Express Scripts' signup link id. Its links name a pharmacy benefit manager,
 * not the member's employer, so the member searches for their carrier (one of
 * Express Scripts' sub-employers) the way a link with no employer searches for
 * the employer. The carrier list itself comes from the server.
 */
export const EXPRESS_SCRIPTS_CLIENT_ID = '27';

/**
 * The parent company whose sub-employers the search lists, or undefined for an
 * ordinary employer search. Only an Express Scripts link that does not already
 * name the sub-employer has one.
 */
export function carrierParentRefFor(params: LegacySignupParams): string | undefined {
  return params.clientId === EXPRESS_SCRIPTS_CLIENT_ID && params.subClientId === undefined
    ? EXPRESS_SCRIPTS_CLIENT_ID
    : undefined;
}

/**
 * Which order the flow follows. A link that names an employer (`client_id` or
 * `sub_client_id`) takes the main path; a link that names none starts with the
 * member's own details and finds the employer by searching, as production does.
 * An Express Scripts link with no sub-employer follows the same search order,
 * searching carriers instead of employers. A member the employer's SSO signed
 * in takes the SSO path.
 */
export type SignupPath = 'employer-link' | 'no-employer' | 'sso';

export function signupPathFor(params: LegacySignupParams): SignupPath {
  if (params.sso) return 'sso';
  const namesNoEmployer = params.clientId === undefined && params.subClientId === undefined;
  return namesNoEmployer || carrierParentRefFor(params) !== undefined
    ? 'no-employer'
    : 'employer-link';
}

// The no-employer path rejoins the main path after the contact step, so its
// tail is read from the main order and later slices' steps join it on their own.
const MAIN_PATH_AFTER_CONTACT = SIGNUP_STEPS.slice(SIGNUP_STEPS.indexOf('contact') + 1);

/**
 * v9 section 2 order. Birth date and sex come before the account (v9 2.2), so
 * the main path's birth-date step is not visited again after the address. A
 * health plan picked in search asks for the plan details next; the search
 * skips that step for any other pick.
 */
export const NO_EMPLOYER_SIGNUP_STEPS = [
  'legal-name',
  'birth-date',
  'account',
  'code',
  'eligibility-check',
  'employer-search',
  'plan-details',
  'substance',
  'address',
  'contact',
  ...MAIN_PATH_AFTER_CONTACT,
] as const satisfies readonly SignupStepId[];

/**
 * The main path with the employer's SSO in place of the account and code
 * steps. Legacy SSO never asked for plan details, and the birth-date step
 * stays: it also asks the sex at birth the profile save needs.
 */
export const SSO_SIGNUP_STEPS = [
  ...SSO_ONLY_STEPS,
  ...SIGNUP_STEPS.filter(
    (step) => step !== 'account' && step !== 'code' && step !== 'plan-details',
  ),
] as const satisfies readonly SignupStepId[];

/** The path's steps, in order. */
export function signupStepsFor(path: SignupPath): readonly SignupStepId[] {
  if (path === 'no-employer') return NO_EMPLOYER_SIGNUP_STEPS;
  return path === 'sso' ? SSO_SIGNUP_STEPS : SIGNUP_STEPS;
}

/** The path's first step. */
export function firstSignupStep(path: SignupPath): SignupStepId {
  return signupStepsFor(path)[0] ?? FIRST_SIGNUP_STEP;
}

/** Whether the path visits `step` at all. */
export function isOnSignupPath(step: SignupStepId, path: SignupPath): boolean {
  return signupStepsFor(path).includes(step);
}

/**
 * Each step sits under the entry route, so moving the flow root moves every
 * step with it. Static export uses trailing slashes, so every step path ends
 * in one.
 */
export function signupStepPath(step: SignupStepId): string {
  return `${SIGNUP_ENTRY_PATH}${step}/`;
}

/**
 * The step after `step` in the path's order, or null for the last step and
 * for a step that path does not visit.
 */
export function nextSignupStep(
  step: SignupStepId,
  path: SignupPath = 'employer-link',
): SignupStepId | null {
  const steps = signupStepsFor(path);
  const at = steps.indexOf(step);
  return at < 0 ? null : (steps[at + 1] ?? null);
}
